import {
  _2dMapCombinedQuote,
  _2dMapQuoteTypes,
} from '@larki/shared/type/order.v2';
import { groupBy, groupByUniq, partitionBy } from '@larki/shared/object';
import { RootState } from '../../utilities';
import { createSelector } from '@reduxjs/toolkit';

const getQuotes = createSelector(
  (state: RootState, jobId: number | null) =>
    jobId ? state.orderv2.quotes.get(jobId) : null,
  (quotes) => quotes ?? { freeUserQuote: null, memberQuote: null }
);

const _getRfqCharges = (quote?: _2dMapCombinedQuote | null) =>
  quote ? Object.values(quote.rfq.composition).flatMap((c) => c.charges) : [];

const _getNonRfqCharges = (quote?: _2dMapCombinedQuote | null) =>
  quote
    ? Object.values(quote.nonRfq.composition).flatMap((c) => c.charges)
    : [];

const _getCombinedCharges = (quote?: _2dMapCombinedQuote | null) =>
  quote ? [..._getRfqCharges(quote), ..._getNonRfqCharges(quote)] : [];

const _getChargesByProduct = (quote?: _2dMapCombinedQuote | null) => {
  const charges = _getCombinedCharges(quote);
  if (charges) {
    return groupByUniq(charges, 'product');
  } else {
    return null;
  }
};

const getFreeUserQuoteChargesByProduct = createSelector(
  (state: RootState, jobId: number | null) =>
    jobId ? state.orderv2.quotes.get(jobId)?.freeUserQuote : null,
  _getChargesByProduct
);

const getMemberQuoteChargesByProduct = createSelector(
  (state: RootState, jobId: number | null) =>
    jobId ? state.orderv2.quotes.get(jobId)?.memberQuote : null,
  _getChargesByProduct
);

const _getQuote = (
  state: RootState,
  jobId: number | null,
  isMember: boolean
) => {
  if (jobId) {
    const quotes = state.orderv2.quotes.get(jobId);
    return isMember ? quotes?.memberQuote : quotes?.freeUserQuote;
  }
  return null;
};

const getRfqCharges = createSelector(_getQuote, _getRfqCharges);

const getNonRfqCharges = createSelector(_getQuote, _getNonRfqCharges);

const getChargesByProduct = createSelector(_getQuote, _getChargesByProduct);

const getDiscounts = createSelector(_getQuote, (quote) => {
  const charges = _getCombinedCharges(quote);

  const all = [...(charges?.values() ?? [])].flatMap((c) => c.appliedDiscounts);
  const [member, nonMember] = partitionBy(
    all,
    ({ metadata: { id } }) =>
      id === 'member_manual' || id === 'member_automatic'
  );

  const total = new Map<_2dMapQuoteTypes['group'], number>();
  for (const discount of all) {
    const { group } = discount.metadata;
    if (group) {
      total.set(group, (total.get(group) ?? 0) + discount.amount);
    }
  }
  const byGroup: Partial<Record<_2dMapQuoteTypes['group'], number>> =
    Object.fromEntries(total);

  return { all, member, nonMember, byGroup };
});

const EMPTY_SET = new Set<string>();

const getOverriddenSet = createSelector(_getQuote, (quote) =>
  quote?.overriddenProducts?.length
    ? new Set(quote.overriddenProducts)
    : EMPTY_SET
);

const getQuoteInputs = (state: RootState, jobId: number | null) =>
  jobId ? (state.orderv2.quotes.get(jobId)?.quoteInputs ?? null) : null;

const getQuoteLoading = (state: RootState, jobId: number | null) =>
  jobId ? (state.orderv2.quoteLoading.get(jobId) ?? false) : false;

const getCustomerSoftware = (state: RootState) =>
  state.orderv2.customerSoftware;

const getComments = (state: RootState) => state.orderv2.comments;

export const orderV2Selectors = {
  getQuotes,
  getFreeUserQuoteChargesByProduct,
  getMemberQuoteChargesByProduct,
  getQuote: _getQuote,
  getQuoteInputs,
  getCharges: _getCombinedCharges,
  getRfqCharges,
  getNonRfqCharges,
  getChargesByProduct,
  getDiscounts,
  getOverriddenSet,
  getQuoteLoading,
  getCustomerSoftware,
  getComments,
};
